"use client"
import { createTheme } from "@mui/material/styles"
import "@mui/x-date-pickers/themeAugmentation"
import { sharedColors } from "./colors"
import { mediaQueries, mediaQueriesValues } from "./mediaQueries"
import "./types"

export const sharedTheme = createTheme({
  direction: "rtl",
  typography: {
    fontFamily: "var(--noto-sans-hebrew)",
    h1: {
      fontSize: "48px",
      lineHeight: "56px",
      fontWeight: "700",
      color: sharedColors.black,
    },
    h2: {
      fontSize: "32px",
      lineHeight: "40px",
      fontWeight: "700",
      color: sharedColors.black,
      [`${mediaQueries.desktopM}`]: {
        fontSize: "40px",
        lineHeight: "48px",
      },
    },
    h3: {
      fontSize: "24px",
      lineHeight: "32px",
      fontWeight: "700",
      color: sharedColors.black,
      [`${mediaQueries.desktopM}`]: {
        fontSize: "32px",
        lineHeight: "40px",
      },
    },
    h4: {
      fontSize: "20px",
      lineHeight: "28px",
      fontWeight: "700",
      [`${mediaQueries.desktopM}`]: {
        fontSize: "24px",
        lineHeight: "32px",
      },
      color: sharedColors.black,
    },
    subtitle1: {
      fontSize: "16px",
      lineHeight: "26px",
      fontWeight: "700",
      [`${mediaQueries.desktopM}`]: {
        fontSize: "20px",
        lineHeight: "28px",
      },
      color: sharedColors.black,
    },
    button: {
      fontSize: "18px",
      lineHeight: "26px",
      fontWeight: "700",
      [`${mediaQueries.desktopM}`]: {
        fontSize: "20px",
        lineHeight: "28px",
      },
      color: sharedColors.black,
    },
    body1: {
      fontSize: "18px",
      lineHeight: "26px",
      fontWeight: "400",
      [`${mediaQueries.desktopM}`]: {
        fontSize: "20px",
        lineHeight: "28px",
      },
      // [`${mediaQueries.desktopXl}`]: {
      //   fontSize: "24px",
      //   lineHeight: "26px",
      // },

      color: sharedColors.black,
    },
    body2: {
      fontSize: "16px",
      lineHeight: "24px",
      fontWeight: "400",
      [`${mediaQueries.desktopM}`]: {
        fontSize: "18px",
        lineHeight: "26px",
      },
      color: sharedColors.black,
    },
    body3: {
      fontSize: "14px",
      lineHeight: "22px",
      fontWeight: "400",
      [`${mediaQueries.desktopM}`]: {
        fontSize: "16px",
        lineHeight: "26px",
      },
      color: sharedColors.black,
    },
    body4: {
      fontSize: "12px",
      lineHeight: "20px",
      fontWeight: "400",
      [`${mediaQueries.desktopM}`]: {
        fontSize: "14px",
        lineHeight: "22px",
      },
      color: sharedColors.black,
    },
    number: {
      fontSize: "64px",
      lineHeight: "72px",
      fontWeight: "700",
      [`${mediaQueries.desktopM}`]: {
        fontSize: "56px",
        lineHeight: "64px",
      },
      color: sharedColors.black,
    },
  },
  palette: {
    primary: {
      main: sharedColors.black,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "::selection": {
          backgroundColor: sharedColors.brandMain,
          color: sharedColors.white,
        },
        "::-moz-selection": {
          backgroundColor: sharedColors.brandMain,
          color: sharedColors.white,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true, // Disable ripple effect for all buttons
      },
      styleOverrides: {
        root: {
          fontWeight: 700,
          padding: "10px 18px",
          minWidth: "110px",
          borderRadius: "12px",
          textTransform: "none",
          "&:focus-visible": {
            border: "1px dashed white",
            outlineOffset: "2px",
            outline: "2px solid black",
          },
        },
        sizeMedium: {
          padding: "10px 18px !important",
          fontSize: "18px !important",
          lineHeight: "26px !important",
          height: "48px",
        },
        sizeLarge: {
          padding: "10px 18px !important",
          fontSize: "20px !important",
          lineHeight: "20px !important",
          height: "48px",
        },
        sizeSmall: {
          fontSize: "14px !important",
          height: "24px !important",
          lineHeight: "normal !important",
          padding: "8px 16px !important",
          borderRadius: "8px !important",
        },
      },
      variants: [
        {
          props: { variant: "primary" },
          style: {
            backgroundColor: sharedColors.brandDark,
            color: sharedColors.white,

            "&:hover,&:focus-visible": {
              boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.32)",
            },
            "&:active": {
              boxShadow: "none",
            },
            "&:disabled": {
              textDecoration: "none",
              color: sharedColors.black,
              backgroundColor: "#F5F6F8",
            },
          },
        },
        {
          props: { variant: "secondary" },
          style: {
            border: `1px solid ${sharedColors.brandDark}`,
            color: sharedColors.brandDark,
            backgroundColor: "transparent",

            "&:hover,&:focus-visible": {
              backgroundColor: "#fff",
              boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.32)",
            },
            "&:active": {},
            "&:disabled": {
              color: sharedColors.black,
              border: `1px solid ${sharedColors.brandDark}`,
            },
          },
        },

        {
          props: { variant: "outlineGreen" },
          style: {
            boxShadow: `inset 0 0 0 1px white`,

            color: sharedColors.white,
            backgroundColor: sharedColors.brandDark,

            "&:hover,&:focus-visible": {
              boxShadow: `inset 0 0 0 2px white`,
            },
            "&:active": {},
            "&:disabled": {
              color: sharedColors.black,
              border: `1px solid ${sharedColors.brandDark}`,
            },
          },
        },
        {
          props: { variant: "white" },
          style: {
            color: sharedColors.brandDark,
            backgroundColor: "white",

            "&:hover,&:focus-visible": {
              boxShadow: `0px 4px 12px 0px #00000029`,
            },
            "&:active": {
              backgroundColor: "#F9FAFB",
              boxShadow: "none",
            },
            "&:disabled": {
              color: "rgba(5, 23, 17, 0.6)",

              backgroundColor: "#F5F6F8",
            },
          },
        },
        {
          props: { variant: "text" },
          style: {},
        },
      ],
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0px 4px 12px 0px #00000029",
          borderRadius: "0 0 8px 8px !important",
          "& .MuiList-root": {
            paddingBottom: 0,
            minWidth: "216px",
          },
          "& .MuiButtonBase-root": {
            padding: 0,
            fontSize: "16px",
            lineHeight: "24px",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          alignItems: "flex-end",
          "&.MuiInput-root::before": {
            borderBottom: `1px solid ${sharedColors.darkBlue}`,
            opacity: 0.3,
          },

          "&.Mui-error::before": {
            borderBottom: `1px solid ${sharedColors.red}`,
            opacity: 1,
          },

          "&.MuiInput-root::after": {
            borderBottom: `1px solid ${sharedColors.blue}`,
          },

          "&.Mui-error::after": {
            borderBottom: `1px solid ${sharedColors.red}`,
            opacity: 1,
          },
          "& svg": {
            width: "23px !important",
            alignSelf: "center",
          },
          // Target root when child has aria-expanded=true
          "&:has([aria-expanded='true'])": {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        },
        input: {
          fontSize: "16px",
          lineHeight: "24px",
          "&:focus::placeholder": {
            fontWeight: "500",
            fontSize: "14px",
            lineHeight: "22px",
          },
          "&::placeholder": {
            fontSize: "16px !important",
            lineHeight: "24px",
            color: sharedColors.black,
            fontWeight: "400",
            opacity: 1,
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          boxSizing: "border-box",
          borderBottom: "none",
          // padding: "12px",
          outline: `1px solid ${sharedColors.borderGray}`,
          borderWidth: 0,
          borderStyle: "solid",
          borderColor: sharedColors.borderGray,
          borderRadius: "12px",

          backgroundColor: "#FFF",
          "&:hover": {
            backgroundColor: "#FFF",
            outline: `1px solid ${sharedColors.black}`,
          },
          "&.Mui-focused": {
            backgroundColor: "#FFF",
            outline: `1px solid ${sharedColors.black}`,
          },
          "&.Mui-error": {
            border: "1px solid #C92418",
            outline: "none",

            "&:hover": {
              outline: "none",
            },
          },

          // Target the root element of the FilledInput component
          "&:after": {
            // Target the ':after' pseudo-element
            borderBottom: "none !important", // Apply the style override
          },
          "&:before": {
            borderBottom: "none !important",
          },
        },
      },
    },
    MuiInputLabel: {
      // Target the InputLabel component

      styleOverrides: {
        root: {
          lineHeight: "normal !important",
          // Define styles for different states
          color: sharedColors.labelGray, // Default label color
          top: "3px",
          "&.Mui-focused": {
            color: sharedColors.labelGray, // Color when the input is focused
          },
          "&.Mui-error": {
            color: sharedColors.labelGray, // Color when there's an error
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        flexContainer: {
          gap: "16px",
        },
        indicator: {
          display: "none",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          height: "48px",
          paddingTop: "8px",
          paddingRight: "16px",
          paddingBottom: "8px",
          paddingLeft: "16px",
          borderRadius: "12px",
          fontSize: "16px",
          lineHeight: "26px",
          color: sharedColors.black,
          border: `1px solid ${sharedColors.borderGray}`,
          textTransform: "none",
          "&.Mui-selected": {
            backgroundColor: sharedColors.brandDark,
            color: sharedColors.white,
          },
          "&:hover, &.Mui-selected:hover": {
            backgroundColor: sharedColors.brandDark,
            color: sharedColors.white,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          boxSizing: "border-box",
          borderBottom: "none",
          outline: `1px solid ${sharedColors.borderGray}`,
          borderWidth: 0,
          borderStyle: "solid",
          borderColor: sharedColors.labelGray,
          borderRadius: "12px",

          backgroundColor: "#FFF",
          "&:hover": {
            backgroundColor: "#FFF",
            outline: `1px solid ${sharedColors.labelGray}`,
          },
          "&.Mui-focused": {
            backgroundColor: "#FFF",
            border: "none",
            outline: `1px solid ${sharedColors.labelGray}`,
          },

          fieldset: {
            border: "none",
            outline: "none",
          },
          "&.Mui-error": {
            fontSize: "14px",
            border: "1px solid #C92418",
            outline: "none",
            "&:hover": {
              outline: "none",
            },
          },

          "&:after": {
            borderBottom: "none !important",
          },
          "&:before": {
            borderBottom: "none !important",
          },
        },
      },
    },
  },
  breakpoints: {
    values: mediaQueriesValues,
  },
})

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true // removes the `xs` breakpoint
    sm: true
    md: true
    lg: true
    xl: true
  }
}

export type Theme = typeof sharedTheme
